import { lazy, Suspense, type ComponentType, type ReactElement } from "react";
import {
  createBrowserRouter,
  Outlet,
  type RouteObject,
} from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { RouteFallback } from "@/components/RouteFallback";

/**
 * Route components are loaded on demand so the initial JS bundle only
 * carries the AppShell + the route the user landed on. Each editor /
 * page becomes its own chunk; the bundler emits ~10 smaller files
 * instead of one 530 kB monolith. The first navigation to a previously
 * unseen route shows the fallback briefly while its chunk streams in.
 *
 * Helper wraps `React.lazy` so the dynamic import returns a default
 * export. Our page files use named exports, so we map them through.
 */
function lazyNamed<T extends string>(
  loader: () => Promise<Record<T, ComponentType>>,
  name: T,
) {
  return lazy(() =>
    loader().then((mod) => ({ default: mod[name] })),
  );
}

// ─────────── Product (authed) routes ───────────
const ProjectsPage = lazyNamed(() => import("@/pages/ProjectsPage"), "ProjectsPage");
const ProjectPage = lazyNamed(() => import("@/pages/ProjectPage"), "ProjectPage");
const ProjectSettingsPage = lazyNamed(
  () => import("@/pages/ProjectSettingsPage"),
  "ProjectSettingsPage",
);
const ScenePage = lazyNamed(() => import("@/pages/ScenePage"), "ScenePage");
const CharacterEditorPage = lazyNamed(
  () => import("@/pages/CharacterEditorPage"),
  "CharacterEditorPage",
);
const LocationEditorPage = lazyNamed(
  () => import("@/pages/LocationEditorPage"),
  "LocationEditorPage",
);
const PropEditorPage = lazyNamed(() => import("@/pages/PropEditorPage"), "PropEditorPage");
const CostumeEditorPage = lazyNamed(
  () => import("@/pages/CostumeEditorPage"),
  "CostumeEditorPage",
);
const SettingsPage = lazyNamed(() => import("@/pages/SettingsPage"), "SettingsPage");
const ProjectScreenplayPage = lazyNamed(
  () => import("@/pages/ProjectScreenplayPage"),
  "ProjectScreenplayPage",
);
const NotFoundPage = lazyNamed(() => import("@/pages/NotFoundPage"), "NotFoundPage");

// ─────────── Marketing + auth routes ───────────
// All public-facing pages mount under MarketingShell.
const LoginPage = lazyNamed(() => import("@/pages/LoginPage"), "LoginPage");
const SignupPage = lazyNamed(() => import("@/pages/SignupPage"), "SignupPage");
const LandingPage = lazyNamed(() => import("@/pages/LandingPage"), "LandingPage");
const PricingPage = lazyNamed(() => import("@/pages/PricingPage"), "PricingPage");
const PrivacyPage = lazyNamed(() => import("@/pages/PrivacyPage"), "PrivacyPage");
const TermsPage = lazyNamed(() => import("@/pages/TermsPage"), "TermsPage");
const ToolsIndexPage = lazyNamed(() => import("@/pages/ToolsIndexPage"), "ToolsIndexPage");
const CharacterPromptBuilderPage = lazyNamed(
  () => import("@/pages/tools/CharacterPromptBuilderPage"),
  "CharacterPromptBuilderPage",
);
const LocationPromptBuilderPage = lazyNamed(
  () => import("@/pages/tools/LocationPromptBuilderPage"),
  "LocationPromptBuilderPage",
);
const PropPromptBuilderPage = lazyNamed(
  () => import("@/pages/tools/PropPromptBuilderPage"),
  "PropPromptBuilderPage",
);
const CostumePromptBuilderPage = lazyNamed(
  () => import("@/pages/tools/CostumePromptBuilderPage"),
  "CostumePromptBuilderPage",
);
const ShotPromptBuilderPage = lazyNamed(
  () => import("@/pages/tools/ShotPromptBuilderPage"),
  "ShotPromptBuilderPage",
);
const ShootingSchedulePage = lazyNamed(
  () => import("@/pages/tools/ShootingSchedulePage"),
  "ShootingSchedulePage",
);
const ScreenplayFormatPage = lazyNamed(
  () => import("@/pages/tools/ScreenplayFormatPage"),
  "ScreenplayFormatPage",
);
const ShotListPage = lazyNamed(
  () => import("@/pages/tools/ShotListPage"),
  "ShotListPage",
);
const ScreenplayEditorPage = lazyNamed(
  () => import("@/pages/tools/ScreenplayEditorPage"),
  "ScreenplayEditorPage",
);
const LoglineGeneratorPage = lazyNamed(
  () => import("@/pages/tools/LoglineGeneratorPage"),
  "LoglineGeneratorPage",
);
const CharacterBioGeneratorPage = lazyNamed(
  () => import("@/pages/tools/CharacterBioGeneratorPage"),
  "CharacterBioGeneratorPage",
);
const VideoPromptGeneratorPage = lazyNamed(
  () => import("@/pages/tools/VideoPromptGeneratorPage"),
  "VideoPromptGeneratorPage",
);

const MarketingShellLazy = lazy(() =>
  import("@/components/layout/MarketingShell").then((mod) => ({
    default: mod.MarketingShell,
  })),
);
const MarketingShellFullBleedLazy = lazy(() =>
  import("@/components/layout/MarketingShell").then((mod) => ({
    default: mod.MarketingShellFullBleed,
  })),
);

function withSuspense(element: ReactElement): ReactElement {
  return <Suspense fallback={<RouteFallback />}>{element}</Suspense>;
}

// ─────────── Route trees ───────────
// Public marketing surface: landing, pricing, legal. Free tools land
// here in subsequent phases.
const marketingRoutes: RouteObject = {
  element: withSuspense(<MarketingShellLazy />),
  children: [
    { path: "/", element: withSuspense(<LandingPage />) },
    { path: "/pricing", element: withSuspense(<PricingPage />) },
    { path: "/privacy", element: withSuspense(<PrivacyPage />) },
    { path: "/terms", element: withSuspense(<TermsPage />) },
    { path: "/tools", element: withSuspense(<ToolsIndexPage />) },
    {
      path: "/tools/character-prompt",
      element: withSuspense(<CharacterPromptBuilderPage />),
    },
    {
      path: "/tools/location-prompt",
      element: withSuspense(<LocationPromptBuilderPage />),
    },
    {
      path: "/tools/prop-prompt",
      element: withSuspense(<PropPromptBuilderPage />),
    },
    {
      path: "/tools/costume-prompt",
      element: withSuspense(<CostumePromptBuilderPage />),
    },
    {
      path: "/tools/shot-prompt",
      element: withSuspense(<ShotPromptBuilderPage />),
    },
    {
      path: "/tools/shooting-schedule",
      element: withSuspense(<ShootingSchedulePage />),
    },
    {
      path: "/tools/screenplay-format",
      element: withSuspense(<ScreenplayFormatPage />),
    },
    {
      path: "/tools/shot-list",
      element: withSuspense(<ShotListPage />),
    },
    { path: "/tools/logline", element: withSuspense(<LoglineGeneratorPage />) },
    {
      path: "/tools/character-bio",
      element: withSuspense(<CharacterBioGeneratorPage />),
    },
    {
      path: "/tools/video-prompt",
      element: withSuspense(<VideoPromptGeneratorPage />),
    },
  ],
};

// Auth surfaces: same shell as marketing, separate branch so SEO /
// sitemap tooling treats them differently.
const authRoutes: RouteObject = {
  element: withSuspense(<MarketingShellLazy />),
  children: [
    { path: "/login", element: withSuspense(<LoginPage />) },
    { path: "/signup", element: withSuspense(<SignupPage />) },
  ],
};

// Product (authed) surface: projects list lives at /projects now,
// freeing `/` for the landing page.
const productRoutes: RouteObject = {
  element: <AppShell />,
  children: [
    { path: "/projects", element: withSuspense(<ProjectsPage />) },
    { path: "/settings", element: withSuspense(<SettingsPage />) },
    {
      // Layout-only route so scene navigation doesn't remount ProjectPage.
      path: "/projects/:id",
      element: <Outlet />,
      children: [
        { index: true, element: withSuspense(<ProjectPage />) },
        { path: "characters/:charId", element: withSuspense(<CharacterEditorPage />) },
        { path: "locations/:locId", element: withSuspense(<LocationEditorPage />) },
        { path: "props/:propId", element: withSuspense(<PropEditorPage />) },
        { path: "costumes/:costId", element: withSuspense(<CostumeEditorPage />) },
        { path: "scenes/:sceneId", element: withSuspense(<ScenePage />) },
        { path: "settings", element: withSuspense(<ProjectSettingsPage />) },
        { path: "screenplay", element: withSuspense(<ProjectScreenplayPage />) },
      ],
    },
    { path: "*", element: withSuspense(<NotFoundPage />) },
  ],
};

// Screenplay editor wants the full viewport (no footer) — separate
// branch with the fullBleed shell variant.
const fullBleedToolRoutes: RouteObject = {
  element: withSuspense(<MarketingShellFullBleedLazy />),
  children: [
    {
      path: "/tools/screenplay-editor",
      element: withSuspense(<ScreenplayEditorPage />),
    },
  ],
};

const routes: RouteObject[] = [
  marketingRoutes,
  authRoutes,
  fullBleedToolRoutes,
  productRoutes,
];

export const router = createBrowserRouter(routes);
